import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Axios } from 'services/axios';
import { Confirm, Loading, Notify } from 'notiflix';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import moment from 'moment';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    ['link'],
    ['clean'],
  ],
};

const toSlug = (str) =>
  str?.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || '';

// ── Category columns ──────────────────────────────────────────────────────
const catColumns = [
  { Header: '#', accessor: 'sr', width: '5%' },
  { Header: 'Name', accessor: 'name' },
  { Header: 'Order', accessor: 'order', width: '8%' },
  { Header: 'Active', accessor: 'active', width: '8%' },
  { Header: 'Created', accessor: 'createdOn', align: 'center' },
  { Header: 'Actions', accessor: 'action', align: 'center' },
];

// ── Page columns ──────────────────────────────────────────────────────────
const pageColumns = [
  { Header: '#', accessor: 'sr', width: '5%' },
  { Header: 'Title', accessor: 'title' },
  { Header: 'Category', accessor: 'category' },
  { Header: 'Slug', accessor: 'slug' },
  { Header: 'Active', accessor: 'active', width: '8%' },
  { Header: 'Created', accessor: 'createdOn', align: 'center' },
  { Header: 'Actions', accessor: 'action', align: 'center' },
];

const LegalPages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') === '1' ? 1 : 0);
  const [search, setSearch] = useState('');

  // ── Categories state ──────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [catDialog, setCatDialog] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);
  const [catActive, setCatActive] = useState(true);

  const catForm = useForm({
    defaultValues: { name: '', listName: '', description: '', order: 0 },
  });

  // ── Pages state ───────────────────────────────────────────────────────
  const [pages, setPages] = useState([]);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    Loading.circle('Loading categories…');
    try {
      const res = await Axios.fetch('/legal/categories');
      setCategories(res.data?.data || []);
    } catch {
      Notify.failure('Failed to load categories');
    } finally {
      Loading.remove();
    }
  }, []);

  const fetchPages = useCallback(async () => {
    Loading.circle('Loading pages…');
    try {
      const res = await Axios.fetch('/legal/pages');
      setPages(res.data?.data || []);
    } catch {
      Notify.failure('Failed to load pages');
    } finally {
      Loading.remove();
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchPages();
  }, [fetchCategories, fetchPages]);

  // ── Category CRUD ─────────────────────────────────────────────────────
  const openAddCat = () => {
    catForm.reset({ name: '', listName: '', description: '', order: 0 });
    setCatActive(true);
    setSelectedCat(null);
    setCatDialog(true);
  };

  const openEditCat = (cat) => {
    catForm.reset({
      name: cat.name,
      listName: cat.listName || '',
      description: cat.description || '',
      order: cat.order ?? 0,
    });
    setCatActive(cat.active !== false);
    setSelectedCat(cat);
    setCatDialog(true);
  };

  const onCatSubmit = async (data) => {
    Loading.circle('Saving…');
    const payload = { ...data, slug: toSlug(data.name), active: catActive };
    const url = selectedCat
      ? `/legal/categories?categoryId=${selectedCat.id}`
      : '/legal/categories';
    try {
      await Axios.post(url, payload);
      Notify.success(selectedCat ? 'Category updated' : 'Category created');
      setCatDialog(false);
      await fetchCategories();
    } catch (err) {
      Notify.failure(err.response?.data?.message || 'Save failed');
    } finally {
      Loading.remove();
    }
  };

  const deleteCat = (id) => {
    Confirm.show('Delete Category', 'Delete this category?', 'Yes', 'No', async () => {
      Loading.circle();
      try {
        const cat = categories.find((c) => c.id === id);
        await Axios.delete(`/legal/categories/${id}?label=${encodeURIComponent(cat?.name || '')}`);
        Notify.success('Category deleted');
        await fetchCategories();
      } catch {
        Notify.failure('Delete failed');
      } finally {
        Loading.remove();
      }
    });
  };

  // ── Page CRUD ─────────────────────────────────────────────────────────
  const togglePageActive = async (page) => {
    Loading.circle();
    try {
      const formData = new FormData();
      formData.append('active', String(!page.active));
      await Axios.post(`/legal/pages?pageId=${page.id}`, formData, { 'Content-Type': 'multipart/form-data' });
      Notify.success(`Page marked as ${!page.active ? 'Active' : 'Inactive'}`);
      await fetchPages();
    } catch {
      Notify.failure('Failed to update status');
    } finally {
      Loading.remove();
    }
  };

  const deletePage = (id) => {
    Confirm.show('Delete Page', 'Delete this legal page?', 'Yes', 'No', async () => {
      Loading.circle();
      try {
        const page = pages.find((p) => p.id === id);
        await Axios.delete(`/legal/pages/${id}?label=${encodeURIComponent(page?.title || '')}`);
        Notify.success('Page deleted');
        await fetchPages();
      } catch {
        Notify.failure('Delete failed');
      } finally {
        Loading.remove();
      }
    });
  };

  // ── Table rows ────────────────────────────────────────────────────────
  const q = search.toLowerCase();
  const filteredCats = tab === 0
    ? categories.filter((c) => !q || c.name?.toLowerCase().includes(q) || c.listName?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q))
    : categories;
  const filteredPages = tab === 1
    ? pages.filter((p) => !q || p.title?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q) || p.category?.name?.toLowerCase().includes(q))
    : pages;

  const catRows = filteredCats.map((cat, i) => ({
    sr: <MDTypography variant="caption">{i + 1}</MDTypography>,
    name: <MDTypography variant="button" fontWeight="medium">{cat.name}</MDTypography>,
    order: <MDTypography variant="caption">{cat.order}</MDTypography>,
    active: (
      <MDBadge
        badgeContent={cat.active ? 'Active' : 'Inactive'}
        color={cat.active ? 'success' : 'error'}
        variant="gradient"
        size="sm"
      />
    ),
    createdOn: <MDTypography variant="caption">{moment(cat.createdAt).calendar()}</MDTypography>,
    action: (
      <MDBox display="flex" gap={1}>
        <MDBadge badgeContent="✏️ Edit" color="success" variant="gradient" size="sm" onClick={() => openEditCat(cat)} style={{ cursor: 'pointer', transition: 'transform 0.15s', display: 'inline-block' }} onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'} onMouseLeave={e => e.currentTarget.style.transform='scale(1)'} />
        <MDBadge badgeContent="🗑 Delete" color="error" variant="gradient" size="sm" onClick={() => deleteCat(cat.id)} style={{ cursor: 'pointer', transition: 'transform 0.15s', display: 'inline-block' }} onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'} onMouseLeave={e => e.currentTarget.style.transform='scale(1)'} />
      </MDBox>
    ),
  }));

  const pageRows = filteredPages.map((page, i) => ({
    sr: <MDTypography variant="caption">{i + 1}</MDTypography>,
    title: <MDTypography variant="button" fontWeight="medium">{page.title}</MDTypography>,
    category: <MDTypography variant="caption">{page.category?.name || '—'}</MDTypography>,
    slug: <MDTypography variant="caption">{page.slug}</MDTypography>,
    active: (
      <MDBox
        onClick={() => togglePageActive(page)}
        title={page.active ? 'Click to deactivate' : 'Click to activate'}
        style={{ cursor: 'pointer', display: 'inline-block', transition: 'transform 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'}
        onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}
      >
        <MDBadge
          badgeContent={page.active ? '✅ Active' : '❌ Inactive'}
          color={page.active ? 'success' : 'error'}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    createdOn: <MDTypography variant="caption">{moment(page.createdAt).calendar()}</MDTypography>,
    action: (
      <MDBox display="flex" gap={1}>
        <MDBadge badgeContent="✏️ Edit" color="success" variant="gradient" size="sm" onClick={() => navigate(`/legal-pages/edit/${page.id}`)} style={{ cursor: 'pointer', transition: 'transform 0.15s', display: 'inline-block' }} onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'} onMouseLeave={e => e.currentTarget.style.transform='scale(1)'} />
        <MDBadge badgeContent="🗑 Delete" color="error" variant="gradient" size="sm" onClick={() => deletePage(page.id)} style={{ cursor: 'pointer', transition: 'transform 0.15s', display: 'inline-block' }} onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'} onMouseLeave={e => e.currentTarget.style.transform='scale(1)'} />
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* ── Page Header ─────────────────────────────────── */}
      <MDBox
        mt={4}
        mb={3}
        px={3}
        py={2.5}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(102,126,234,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="white" mb={0.5}>
            ⚖️ Legal Pages
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Manage legal categories and policy pages
          </MDTypography>
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <MDBox
            sx={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '10px',
              px: 2,
              py: 0.5,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <MDTypography variant="caption" color="white">
              📁 {categories.length} Categories &nbsp;·&nbsp; 📄 {pages.length} Pages
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* ── Search + Tabs row ───────────────────────────── */}
      <MDBox
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          placeholder={tab === 0 ? 'Search categories…' : 'Search pages…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{
            width: 340,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 16px rgba(102,126,234,0.2)' },
              '&.Mui-focused': { boxShadow: '0 4px 16px rgba(102,126,234,0.3)' },
            },
          }}
          InputProps={{
            startAdornment: (
              <span style={{ marginRight: 8, color: '#667eea', fontSize: 16 }}>🔍</span>
            ),
            endAdornment: search ? (
              <span
                onClick={() => setSearch('')}
                style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1 }}
                title="Clear"
              >
                ✕
              </span>
            ) : null,
          }}
        />

        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setSearch(''); }}
          sx={{
            background: '#f1f5f9',
            borderRadius: '12px',
            p: 0.5,
            minHeight: 'unset',
            '& .MuiTabs-indicator': {
              height: '100%',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              zIndex: 0,
            },
            '& .MuiTab-root': {
              minHeight: 36,
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '0.8rem',
              zIndex: 1,
              transition: 'color 0.2s',
              color: '#64748b',
              '&.Mui-selected': { color: '#fff' },
            },
          }}
        >
          <Tab label={`📁 Categories${tab === 0 && search && filteredCats.length !== categories.length ? ` (${filteredCats.length})` : ` (${categories.length})`}`} />
          <Tab label={`📄 Pages${tab === 1 && search && filteredPages.length !== pages.length ? ` (${filteredPages.length})` : ` (${pages.length})`}`} />
        </Tabs>
      </MDBox>

      {/* ── Categories tab ─────────────────────────────── */}
      {tab === 0 && (
        <MDBox
          sx={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <MDBox
            px={3}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderBottom: '1px solid #f1f5f9' }}
          >
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold">Legal Categories</MDTypography>
              <MDTypography variant="caption" color="text">
                {filteredCats.length} {filteredCats.length === 1 ? 'category' : 'categories'}{search ? ` matching "${search}"` : ''}
              </MDTypography>
            </MDBox>
            <MDButton
              variant="gradient"
              color="info"
              onClick={openAddCat}
              sx={{ borderRadius: '10px', boxShadow: '0 4px 12px rgba(102,126,234,0.3)', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 16px rgba(102,126,234,0.4)' }, transition: 'all 0.2s' }}
            >
              + Add Category
            </MDButton>
          </MDBox>
          {catRows.length > 0
            ? <DataTable table={{ columns: catColumns, rows: catRows }} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
            : (
              <MDBox py={8} textAlign="center">
                <MDTypography variant="h2" mb={1}>📁</MDTypography>
                <MDTypography variant="h6" color="text">{search ? `No categories match "${search}"` : 'No categories yet.'}</MDTypography>
                {!search && <MDTypography variant="caption" color="text">Click "+ Add Category" to get started.</MDTypography>}
              </MDBox>
            )
          }
        </MDBox>
      )}

      {/* ── Pages tab ──────────────────────────────────── */}
      {tab === 1 && (
        <MDBox
          sx={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <MDBox
            px={3}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderBottom: '1px solid #f1f5f9' }}
          >
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold">Legal Pages</MDTypography>
              <MDTypography variant="caption" color="text">
                {filteredPages.length} {filteredPages.length === 1 ? 'page' : 'pages'}{search ? ` matching "${search}"` : ''}
              </MDTypography>
            </MDBox>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => navigate('/legal-pages/new')}
              sx={{ borderRadius: '10px', boxShadow: '0 4px 12px rgba(102,126,234,0.3)', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 16px rgba(102,126,234,0.4)' }, transition: 'all 0.2s' }}
            >
              + Add Page
            </MDButton>
          </MDBox>
          {pageRows.length > 0
            ? <DataTable table={{ columns: pageColumns, rows: pageRows }} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
            : (
              <MDBox py={8} textAlign="center">
                <MDTypography variant="h2" mb={1}>📄</MDTypography>
                <MDTypography variant="h6" color="text">{search ? `No pages match "${search}"` : 'No legal pages yet.'}</MDTypography>
                {!search && <MDTypography variant="caption" color="text">Click "+ Add Page" to create your first legal page.</MDTypography>}
              </MDBox>
            )
          }
        </MDBox>
      )}

      {/* ── Category Dialog ────────────────────────────── */}
      <Dialog open={catDialog} onClose={() => setCatDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCat ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <form onSubmit={catForm.handleSubmit(onCatSubmit)}>
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={catForm.control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Category Name *"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="listName"
                  control={catForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="List Name (sidebar label)"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText="Short label shown in the left sidebar on the Legal Center page"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={catForm.control}
                  render={({ field }) => (
                    <TextField {...field} label="Description" fullWidth multiline rows={2} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="order"
                  control={catForm.control}
                  render={({ field }) => (
                    <TextField {...field} label="Display Order" type="number" fullWidth InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={catActive} onChange={(e) => setCatActive(e.target.checked)} color="primary" />}
                  label="Active"
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 2 }}>
              <MDButton onClick={() => setCatDialog(false)} color="secondary" variant="outlined">Cancel</MDButton>
              <MDButton type="submit" color="info" variant="contained">
                {selectedCat ? 'Update' : 'Create'}
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default LegalPages;
